import React, { useEffect, useMemo, useState } from 'react'
import Masonry from 'react-masonry-css'
import classes from './Menu.module.scss'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../base/customHooks/componentHOC'
import { useDragAutoScroll } from '../../../../base/customHooks/useDragAutoScroll'

import { CatalogItem, MenuCategory, MenuItem, UserMenuJson } from '../models/models'
import { GetAdminMenuCatalogApi } from '../services/GetAdminMenuCatalogApi'
import { GetAdminUserMenuApi } from '../services/GetAdminUserMenuApi'
import { SaveAdminUserMenuApi } from '../services/SaveAdminUserMenuApi'
import List from '../../../../layouts/components/admin/Menu/List'
import IPSelectBox from '../../../../components/IPSelectBox'
import { SetDefault } from '../services/SetDefaultApi'

const FAV_NAME = 'Sık Kullanılanlar'

// --- Yardımcı Fonksiyonlar ---
function unwrapRoot(res: any): any {
  if (res == null) return res
  return res?.Data ?? res?.data ?? res?.Result ?? res?.result ?? res?.Value ?? res?.value ?? res
}
function deepClone<T>(x: T): T { return JSON.parse(JSON.stringify(x)) }
function catId(x: any): number { return Number(x?.Id ?? x?.id ?? 0) }
function catName(x: any): string { return String(x?.Name ?? x?.name ?? '') }
function catLink(x: any): string { return String(x?.Link ?? x?.link ?? '') }

function normalizeCategory(x: any): MenuCategory {
  const rawSub = x?.subMenu ?? x?.SubMenu ?? []
  return {
    id: Number(x?.id ?? x?.Id ?? 0),
    name: String(x?.name ?? x?.Name ?? ''),
    link: String(x?.link ?? x?.Link ?? '#'),
    subMenu: Array.isArray(rawSub) ? rawSub.filter(Boolean).map((it: any) => ({
      id: Number(it.id ?? it.Id ?? 0),
      name: String(it.name ?? it.Name ?? ''),
      link: String(it.link ?? it.Link ?? '')
    })) : []
  }
}

const Index = (props: BaseComponentWithContextProps) => {
  // Native HTML5 sürükle-bırak sayfayı kendiliğinden kaydırmıyor; bu olmadan ekranda
  // görünenden uzun bir listede öğeyi yukarı taşımak mümkün değil.
  useDragAutoScroll()

  const userId = props.user.UserId
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [catalog, setCatalog] = useState<CatalogItem[]>([])
  const [userJson, setUserJson] = useState<UserMenuJson>({ menu: [] })
  const [collapsedCats, setCollapsedCats] = useState<number[]>([])
  const [isFavOpen, setIsFavOpen] = useState(false) // Sağ panel kontrolü
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  type CatalogOption = { value: number; label: string }

  const [selectedCatalogByCat, setSelectedCatalogByCat] = useState<Record<number, CatalogOption | null>>({})

  // --- Veri Ayrıştırma ---
  const favCategory = useMemo(() => userJson.menu.find(c => (c.name ?? '').trim() === FAV_NAME), [userJson])
  const otherCategories = useMemo(() => userJson.menu.filter(c => (c.name ?? '').trim() !== FAV_NAME), [userJson])

  const favoriteIds = useMemo(() => {
    return favCategory ? favCategory.subMenu.map(i => Number(i.id)) : []
  }, [favCategory])

  useEffect(() => {
    const init = async () => {
      try {
        const [cRes, uRes] = await Promise.all([
          new GetAdminMenuCatalogApi({}).GetAdminMenuCatalogApi(),
          new GetAdminUserMenuApi({}, userId).GetAdminUserMenuApi()
        ])
        const cData = unwrapRoot(cRes); setCatalog(Array.isArray(cData) ? cData : [])
        const uData = unwrapRoot(uRes)
        const menuArr = (uData?.Menu ?? uData?.menu ?? (Array.isArray(uData) ? uData : [])).map(normalizeCategory)

        if (!menuArr.some(c => (c.name ?? '').trim() === FAV_NAME)) {
          menuArr.unshift({ id: 999999, name: FAV_NAME, link: '#', subMenu: [] })
        }
        setUserJson({ menu: menuArr })
      } catch (e: any) { setError(e.message) }
      finally { setLoading(false) }
    }
    init()
  }, [userId])

  // --- Aksiyonlar ---
  const toggleCollapse = (id: number) => {
    setCollapsedCats(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const addCategory = () => {
    setUserJson(prev => {
      const cloned = deepClone(prev)
      const maxId = cloned.menu.reduce((acc, c) => Math.max(acc, Number(c.id)), 0)
      cloned.menu.push({ id: maxId + 1, name: 'Yeni Kategori', link: '#', subMenu: [] })
      return cloned
    })
  }

  // --- Ortak yardımcı: API dönüşünden menüyü state'e bas ---
  function applyMenuFromApiResponse(res: any) {
    const data = unwrapRoot(res)

    // API bazen { Menu: [...] } bazen direkt array döndürebiliyor:
    const rawMenu = data?.Menu ?? data?.menu ?? (Array.isArray(data) ? data : null)
    if (!rawMenu || !Array.isArray(rawMenu)) return false

    const menuArr = rawMenu.map(normalizeCategory)

    // Favoriler yoksa ekle
    if (!menuArr.some(c => (c.name ?? '').trim() === FAV_NAME)) {
      menuArr.unshift({ id: 999999, name: FAV_NAME, link: '#', subMenu: [] })
    }

    setUserJson({ menu: menuArr })
    return true
  }

  const deleteCategory = (id: number) => {
    setUserJson(prev => {
      const cloned = deepClone(prev)
      cloned.menu = cloned.menu.filter(c => Number(c.id) !== id || (c.name ?? '').trim() === FAV_NAME)
      return cloned
    })
  }

  const moveCategory = (dragId: number, targetId: number) => {
    if (dragId === targetId) return
    setUserJson(prev => {
      const cloned = deepClone(prev)
      const dragIdx = cloned.menu.findIndex(c => Number(c.id) === dragId)
      const targetIdx = cloned.menu.findIndex(c => Number(c.id) === targetId)
      if (dragIdx === -1 || targetIdx === -1) return prev
      const [moved] = cloned.menu.splice(dragIdx, 1)
      cloned.menu.splice(targetIdx, 0, moved)
      return cloned
    })
  }

  const moveItem = (fromCatId: number, itemId: number, toCatId: number, targetIdx: number) => {
    setUserJson(prev => {
      const cloned = deepClone(prev)
      const fromCat = cloned.menu.find(c => Number(c.id) === fromCatId)
      const toCat = cloned.menu.find(c => Number(c.id) === toCatId)

      if (!toCat) return prev

      const isToFav = (toCat.name ?? '').trim() === FAV_NAME
      const isFromFav = fromCat ? (fromCat.name ?? '').trim() === FAV_NAME : false
      const existsInTarget = toCat.subMenu.some(i => Number(i.id) === itemId)

      let itemObj: MenuItem | null = null

      if (fromCat) {
        const sourceIdx = fromCat.subMenu.findIndex(i => Number(i.id) === itemId)
        if (sourceIdx !== -1) {
          if (fromCatId !== toCatId && existsInTarget) return prev;

          if (fromCatId === toCatId || (!isToFav && !isFromFav)) {
            [itemObj] = fromCat.subMenu.splice(sourceIdx, 1)
          } else {
            itemObj = { ...fromCat.subMenu[sourceIdx] }
          }
        }
      }

      if (!itemObj) {
        const catalogItem = catalog.find(x => catId(x) === itemId)
        if (catalogItem) {
          if (existsInTarget) return prev;
          itemObj = { id: itemId, name: catName(catalogItem), link: catLink(catalogItem) }
        }
      }

      if (itemObj && (fromCatId === toCatId || !existsInTarget)) {
        toCat.subMenu.splice(targetIdx, 0, itemObj)
      }

      return cloned
    })
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const payload = {
        Menu: userJson.menu.map(c => ({
          Id: c.id,
          Name: c.name,
          Link: c.link,
          SubMenu: (c.subMenu || []).map(i => ({ Id: i.id, Name: i.name, Link: i.link }))
        }))
      }

      const res = await new SaveAdminUserMenuApi({}, userId).SaveAdminUserMenuApi(payload as any)

      // 1) API güncel menüyü dönüyorsa state'i onunla güncelle
      const applied = applyMenuFromApiResponse(res)

      // 2) Dönmüyorsa garanti olsun diye re-fetch (opsiyonel ama sağlam)
      if (!applied) {
        const uRes = await new GetAdminUserMenuApi({}, userId).GetAdminUserMenuApi()
        applyMenuFromApiResponse(uRes)
      }

      setMode('view')
    } catch (e: any) {
      setError(e?.message ?? 'Kaydetme hatası oluştu.')
    } finally {
      setSaving(false)
    }
  }

  const setDefault = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await new SetDefault({}, userId).SetDefault()

      // 1) API güncel menüyü dönüyorsa state'i onunla güncelle
      const applied = applyMenuFromApiResponse(res)

      // 2) Dönmüyorsa re-fetch
      if (!applied) {
        const uRes = await new GetAdminUserMenuApi({}, userId).GetAdminUserMenuApi()
        applyMenuFromApiResponse(uRes)
      }

      setMode('view')
    } catch (e: any) {
      setError(e?.message ?? 'Kaydetme hatası oluştu.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div style={{ padding: 20 }}>Yükleniyor...</div>

  return (
    <div className={classes.container}>
      {/* Üst Bar */}
      <div className={classes.headerBar}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button onClick={() => setMode(mode === 'view' ? 'edit' : 'view')} className={classes.actionBtn}>
            {mode === 'view' ? '⚙️ Düzenle' : '👁️ Görüntüle'}
          </button>
          {mode === 'edit' && (
            <>
              <button onClick={addCategory} className={classes.actionBtn}>+ Kategori Ekle</button>
              <button onClick={save} disabled={saving} className={`${classes.actionBtn} ${classes.saveBtn}`}>
                {saving ? '...' : 'Kaydet'}
              </button>
              <button onClick={setDefault} disabled={saving} className={`${classes.actionBtn} ${classes.saveBtn}`}>
                {saving ? '...' : 'Sıfırla'}
              </button>
            </>
          )}
        </div>

        {/* Sık Kullanılanlar Tetikleyici */}
        <button
          className={`${classes.favToggle} ${isFavOpen ? classes.active : ''}`}
          onClick={() => setIsFavOpen(!isFavOpen)}
        >
          ⭐ Sık Kullanılanlar
        </button>
      </div>

      {/* Sağ Açılır Menü (Drawer) */}
      <div className={`${classes.favDrawer} ${isFavOpen ? classes.show : ''}`}>
        <div className={classes.drawerHeader}>
          <span>⭐ Sık Kullanılanlar</span>
          <button onClick={() => setIsFavOpen(false)}>✕</button>
        </div>
        <div className={classes.drawerBody}>
          {favCategory && (
            <List dataList={favCategory} allFavorites={favoriteIds} />
          )}
          {favCategory?.subMenu.length === 0 && (
            <div className={classes.emptyInfo}>Henüz favori eklenmedi.</div>
          )}
        </div>
      </div>

      {/* Karartma Overlay (Drawer açıkken) */}
      {isFavOpen && <div className={classes.overlay} onClick={() => setIsFavOpen(false)} />}

      {/* Ana İçerik */}
      <div className={classes.content}>
        {mode === 'edit' ? (
          <div className={classes.editModeGrid}>
            {userJson.menu.map((cat) => {
              const isFav = (cat.name ?? '').trim() === FAV_NAME
              const isCollapsed = collapsedCats.includes(Number(cat.id))
              const availableInCatalog = catalog.filter(ci => !cat.subMenu.some(si => Number(si.id) === catId(ci)))
              const catalogOptions = availableInCatalog.map(ci => ({
                value: catId(ci),
                label: catName(ci)
              }))

              return (
                <div
                  key={cat.id}
                  className={`${classes.editCard} ${isFav ? classes.favCard : ''}`}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => {
                    const raw = e.dataTransfer.getData('application/json')
                    if (!raw) return
                    const data = JSON.parse(raw)
                    if (data.kind === 'category') moveCategory(data.id, Number(cat.id))
                    else if (data.kind === 'item') moveItem(data.fromCatId, data.itemId, Number(cat.id), cat.subMenu.length)
                  }}
                >
                  <div className={classes.cardHeader}>
                    <div
                      draggable
                      onDragStart={e => e.dataTransfer.setData('application/json', JSON.stringify({ kind: 'category', id: cat.id }))}
                      className={classes.dragHandle}
                    >⋮⋮</div>
                    <button className={classes.collapseBtn} onClick={() => toggleCollapse(Number(cat.id))}>
                      {isCollapsed ? '▶' : '▼'}
                    </button>
                    <input
                      className={classes.catInput}
                      value={cat.name}
                      disabled={isFav}
                      onChange={e => {
                        const cloned = deepClone(userJson);
                        const target = cloned.menu.find(c => c.id === cat.id);
                        if (target) { target.name = e.target.value; setUserJson(cloned); }
                      }}
                    />
                    {!isFav && (
                      <button className={classes.deleteBtn} onClick={() => deleteCategory(Number(cat.id))}>✕</button>
                    )}
                  </div>

                  {!isCollapsed && (
                    <div className={classes.cardContent}>
                      <IPSelectBox
                        classNamePrefix="catalogSelect"
                        className={classes.catalogSelectReact}
                        placeholder="+ Katalogdan Seç..."
                        isClearable
                        isSearchable
                        options={catalogOptions}
                        value={selectedCatalogByCat[Number(cat.id)] ?? null}
                        onChangeSingle={(opt) => {
                          if (!opt) {
                            setSelectedCatalogByCat(prev => ({ ...prev, [Number(cat.id)]: null }))
                            return
                          }

                          moveItem(0, Number(opt.value), Number(cat.id), cat.subMenu.length)

                          // Seçimden sonra boşalt
                          setSelectedCatalogByCat(prev => ({ ...prev, [Number(cat.id)]: null }))
                        }}
                      />


                      <div className={classes.itemContainer}>
                        {cat.subMenu.map((it, idx) => (
                          <div
                            key={`${it.id}-${idx}`}
                            draggable
                            className={classes.draggableItem}
                            onDragStart={e => {
                              e.stopPropagation();
                              e.dataTransfer.setData('application/json', JSON.stringify({ kind: 'item', fromCatId: cat.id, itemId: it.id }))
                            }}
                            onDragOver={e => { e.preventDefault(); (e.currentTarget as any).style.borderTop = '2px solid #007bff' }}
                            onDragLeave={e => (e.currentTarget as any).style.borderTop = 'none'}
                            onDrop={e => {
                              e.preventDefault(); e.stopPropagation();
                              (e.currentTarget as any).style.borderTop = 'none';
                              const raw = e.dataTransfer.getData('application/json')
                              if (!raw) return
                              const data = JSON.parse(raw)
                              if (data.kind === 'item') moveItem(data.fromCatId, data.itemId, Number(cat.id), idx)
                            }}
                          >
                            <span>{it.name}</span>
                            <button onClick={() => {
                              const cloned = deepClone(userJson);
                              const target = cloned.menu.find(x => x.id === cat.id);
                              if (target) { target.subMenu.splice(idx, 1); setUserJson(cloned); }
                            }}>✕</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <Masonry
            breakpointCols={{ default: 4, 1100: 3, 700: 2, 500: 1 }}
            className={classes['my-masonry-grid']}
            columnClassName={classes['my-masonry-grid_column']}
          >
            {otherCategories.map(cat => (
              <div key={cat.id} className={classes.dataList}>
                <List dataList={cat} allFavorites={favoriteIds} />
              </div>
            ))}
          </Masonry>
        )}
      </div>
    </div>
  )
}

export default componentWithContext(Index)